/*
 * <recipe-hub> — client-side search, faceting, sort and "show more" for
 * Recipe metaobjects. Shopify exposes at most 250 metaobjects per Liquid
 * pagination page, so any remaining section pages are loaded before filters
 * are initialised.
 */
class RecipeHub extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.grid = this.querySelector('[data-recipe-grid]');
    if (!this.grid) return;

    this.filtersForm = this.querySelector('[data-recipe-filters]');
    this.filtersHome = this.querySelector('[data-recipe-filters-home]');
    this.filtersDrawer = this.querySelector('[data-recipe-filters-drawer]');
    this.filtersSidebar = this.querySelector('.recipe-hub__filters-drawer');
    this.mobileFiltersQuery = window.matchMedia('(max-width: 767px)');
    this.positionFilters = this.positionFilters.bind(this);
    this.positionFilters();
    this.mobileFiltersQuery.addEventListener('change', this.positionFilters);

    this.searchInput = this.querySelector('[data-recipe-search]');
    this.navGroupOptions = this.querySelector('[data-recipe-nav-group-options]');
    this.tagOptions = this.querySelector('[data-recipe-tag-options]');
    this.countEl = this.querySelector('[data-recipe-count]');
    this.emptyEl = this.querySelector('[data-recipe-empty]');
    this.moreBtn = this.querySelector('[data-recipe-more]');
    this.moreContainer = this.moreBtn?.closest('.recipe-hub__more');
    this.clearBtn = this.querySelector('[data-recipe-clear]');
    this.drawerClearBtn = this.querySelector('[data-recipe-drawer-clear]');
    this.activeFilters = this.querySelector('[data-recipe-active-filters]');
    this.filterPillTemplate = this.querySelector('[data-recipe-filter-pill-template]');

    this.batch = parseInt(this.dataset.batch, 10) || 24;
    this.total = parseInt(this.dataset.total, 10) || 0;
    this.visible = this.batch;
    this.query = new URLSearchParams(window.location.search).get('recipe_q')?.trim() || '';
    if (this.searchInput) this.searchInput.value = this.query;
    this.sort = this.querySelector('[data-recipe-sort]:checked')?.value || 'date-desc';
    this.selectedNavGroups = new Set();
    this.selectedTags = new Set();
    this.ranges = {};

    await this.loadRemainingPages();
    this.buildCards();
    this.buildNavGroupOptions();
    this.buildTagOptions();
    this.initializeRanges();
    this.applyUrlFilters();
    this.bindEvents();
    this.render();
  }

  disconnectedCallback() {
    this.mobileFiltersQuery?.removeEventListener('change', this.positionFilters);
  }

  positionFilters() {
    if (!this.filtersForm || !this.filtersHome || !this.filtersDrawer) return;

    const destination = this.mobileFiltersQuery.matches ? this.filtersDrawer : this.filtersHome;
    if (this.filtersForm.parentElement !== destination) destination.appendChild(this.filtersForm);

    if (!this.mobileFiltersQuery.matches && this.filtersSidebar?.opened) {
      this.filtersSidebar.hide();
    }
  }

  async loadRemainingPages() {
    let nextUrl = this.grid.dataset.nextUrl;

    while (nextUrl) {
      try {
        const url = new URL(nextUrl, window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Recipe page request returned ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = documentFragment.querySelector('[data-recipe-grid]');
        if (!nextGrid) break;

        nextGrid.querySelectorAll('[data-recipe-card]').forEach((card) => this.grid.appendChild(card));
        nextUrl = nextGrid.dataset.nextUrl || '';
      } catch (error) {
        console.warn('Could not load all Recipe Hub entries.', error);
        break;
      }
    }

    delete this.grid.dataset.nextUrl;
  }

  buildCards() {
    this.cards = Array.from(this.grid.querySelectorAll('[data-recipe-card]')).map((el) => {
      const tags = (el.dataset.tags || '').split('|').filter(Boolean);
      const tagLabels = (el.dataset.tagLabels || '').split('|');
      const navGroups = (el.dataset.navGroups || '').split('|').filter(Boolean);
      const navGroupLabels = (el.dataset.navGroupLabels || '').split('|');

      return {
        el,
        title: (el.dataset.title || '').trim(),
        tags,
        tagLabels,
        navGroups,
        navGroupLabels,
        searchableTags: tagLabels.join(' ').toLowerCase(),
        serves: this.numberOrNull(el.dataset.serves),
        prep: this.numberOrNull(el.dataset.prep),
        cook: this.numberOrNull(el.dataset.cook),
        date: parseInt(el.dataset.date, 10) || 0,
      };
    });
  }

  numberOrNull(value) {
    const number = Number(value);
    return value !== '' && Number.isFinite(number) ? number : null;
  }

  buildNavGroupOptions() {
    if (!this.navGroupOptions) return;

    const navGroups = new Map();
    this.cards.forEach((card) => {
      new Set(card.navGroups).forEach((handle) => {
        const index = card.navGroups.indexOf(handle);
        const label = card.navGroupLabels[index] || handle.replaceAll('-', ' ');
        const entry = navGroups.get(handle) || { handle, label, count: 0 };
        entry.count += 1;
        navGroups.set(handle, entry);
      });
    });

    this.navGroupOptions.replaceChildren();

    Array.from(navGroups.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((navGroup, index) => {
        const item = document.createElement('li');
        item.className = 'list-menu__item facets__item';

        const label = document.createElement('label');
        label.className = 'facet-checkbox';
        label.htmlFor = `recipe-nav-group-${this.dataset.sectionId}-${index}`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'styled-checkbox';
        input.id = label.htmlFor;
        input.value = navGroup.handle;
        input.dataset.recipeNavGroup = '';
        input.dataset.recipeLabel = navGroup.label;

        label.append(input, document.createTextNode(`${navGroup.label} (${navGroup.count})`));
        item.appendChild(label);
        this.navGroupOptions.appendChild(item);
      });
  }

  buildTagOptions() {
    if (!this.tagOptions) return;

    const tags = new Map();
    this.cards.forEach((card) => {
      new Set(card.tags).forEach((handle) => {
        const index = card.tags.indexOf(handle);
        const label = card.tagLabels[index] || handle.replaceAll('-', ' ');
        const entry = tags.get(handle) || { handle, label, count: 0 };
        entry.count += 1;
        tags.set(handle, entry);
      });
    });

    this.tagOptions.replaceChildren();

    Array.from(tags.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((tag, index) => {
        const item = document.createElement('li');
        item.className = 'list-menu__item facets__item';

        const label = document.createElement('label');
        label.className = 'facet-checkbox';
        label.htmlFor = `recipe-tag-${this.dataset.sectionId}-${index}`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'styled-checkbox';
        input.id = label.htmlFor;
        input.value = tag.handle;
        input.dataset.recipeTag = '';
        input.dataset.recipeLabel = tag.label;

        label.append(input, document.createTextNode(`${tag.label} (${tag.count})`));
        item.appendChild(label);
        this.tagOptions.appendChild(item);
      });
  }

  initializeRanges() {
    this.querySelectorAll('[data-recipe-range]').forEach((range) => {
      const field = range.dataset.recipeRange;
      const values = this.cards.map((card) => card[field]).filter((value) => value !== null);
      const min = values.length ? Math.min(...values) : 0;
      const max = values.length ? Math.max(...values) : 0;
      const minInput = range.querySelector('[data-recipe-range-min]');
      const maxInput = range.querySelector('[data-recipe-range-max]');

      [minInput, maxInput].forEach((input) => {
        input.min = min;
        input.max = max;
        input.disabled = min === max;
      });
      minInput.value = min;
      maxInput.value = max;

      this.ranges[field] = {
        element: range,
        minInput,
        maxInput,
        output: range.querySelector('[data-recipe-range-output]'),
        minOutput: range.querySelector('[data-recipe-range-min-output]'),
        maxOutput: range.querySelector('[data-recipe-range-max-output]'),
        label: range.dataset.label || field,
        suffix: range.dataset.suffix || '',
        lowerBound: min,
        upperBound: max,
      };

      this.updateRange(field);
    });
  }

  applyUrlFilters() {
    const params = new URLSearchParams(window.location.search);
    const selectedOptions = (key, selector) => {
      const inputs = Array.from(this.querySelectorAll(selector));
      const available = new Set(inputs.map((input) => input.value));
      const selected = new Set(
        (params.get(key) || '').split(',').map((value) => value.trim()).filter((value) => available.has(value))
      );
      inputs.forEach((input) => { input.checked = selected.has(input.value); });
      return selected;
    };

    this.selectedNavGroups = selectedOptions('recipe_type', '[data-recipe-nav-group]');
    this.selectedTags = selectedOptions('recipe_tag', '[data-recipe-tag]');

    const sortInput = Array.from(this.querySelectorAll('[data-recipe-sort]')).find((input) => input.value === params.get('sort'));
    if (sortInput) {
      sortInput.checked = true;
      this.sort = sortInput.value;
    }

    Object.entries(this.ranges).forEach(([field, range]) => {
      const valueFor = (key, fallback) => {
        const value = Number(params.get(key));
        return params.has(key) && Number.isFinite(value)
          ? Math.min(Math.max(value, range.lowerBound), range.upperBound)
          : fallback;
      };
      const minimum = valueFor(`${field}_min`, range.lowerBound);
      const maximum = Math.max(minimum, valueFor(`${field}_max`, range.upperBound));

      range.minInput.value = minimum;
      range.maxInput.value = maximum;
      this.updateRange(field);
    });
  }

  bindEvents() {
    this.filtersForm?.addEventListener('submit', (event) => event.preventDefault());

    if (this.searchInput) {
      let timer;
      this.searchInput.addEventListener('input', (event) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          this.query = event.target.value.trim();
          this.updateFiltersUrl();
          this.resetVisibleAndRender();
        }, 150);
      });
    }

    this.filtersForm?.addEventListener('input', (event) => {
      if (event.target.matches('[data-recipe-search]')) return;

      if (event.target.matches('[data-recipe-tag]')) {
        this.selectedTags = new Set(
          Array.from(this.querySelectorAll('[data-recipe-tag]:checked')).map((input) => input.value)
        );
      }

      if (event.target.matches('[data-recipe-nav-group]')) {
        this.selectedNavGroups = new Set(
          Array.from(this.querySelectorAll('[data-recipe-nav-group]:checked')).map((input) => input.value)
        );
      }

      if (event.target.matches('[data-recipe-sort]')) {
        this.sort = event.target.value;
      }

      const rangeElement = event.target.closest('[data-recipe-range]');
      if (rangeElement) {
        this.constrainRange(rangeElement.dataset.recipeRange, event.target);
      }

      this.updateFiltersUrl();
      this.resetVisibleAndRender();
    });

    this.moreBtn?.addEventListener('click', () => {
      this.visible += this.batch;
      this.render();
    });

    this.clearBtn?.addEventListener('click', () => this.clearFilters());
    this.drawerClearBtn?.addEventListener('click', () => this.clearFilters());
    this.activeFilters?.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-recipe-filter-remove]');
      if (pill) this.clearSingleFilter(pill.dataset.filterType, pill.dataset.filterValue);
    });
  }

  constrainRange(field, changedInput) {
    const range = this.ranges[field];
    let minimum = Number(range.minInput.value);
    let maximum = Number(range.maxInput.value);

    if (minimum > maximum) {
      if (changedInput === range.minInput) {
        range.maxInput.value = minimum;
        maximum = minimum;
      } else {
        range.minInput.value = maximum;
        minimum = maximum;
      }
    }

    this.updateRange(field);
  }

  updateRange(field) {
    const range = this.ranges[field];
    const minimum = Number(range.minInput.value);
    const maximum = Number(range.maxInput.value);
    const isActive = minimum > range.lowerBound || maximum < range.upperBound;

    range.element.classList.toggle('is-active', isActive);
    range.output.textContent = isActive ? `${minimum}${range.suffix} – ${maximum}${range.suffix}` : 'Any';
    if (range.minOutput) range.minOutput.textContent = `${minimum}${range.suffix}`;
    if (range.maxOutput) range.maxOutput.textContent = `${maximum}${range.suffix}`;

    const span = Math.max(range.upperBound - range.lowerBound, 1);
    const start = ((minimum - range.lowerBound) / span) * 100;
    const end = ((maximum - range.lowerBound) / span) * 100;
    range.element.style.setProperty('--range-start', `${start}%`);
    range.element.style.setProperty('--range-end', `${end}%`);
  }

  rangeIsActive(range) {
    return Number(range.minInput.value) > range.lowerBound || Number(range.maxInput.value) < range.upperBound;
  }

  hasActiveFilters() {
    return Boolean(this.query || this.selectedNavGroups.size || this.selectedTags.size || Object.values(this.ranges).some((range) => this.rangeIsActive(range)));
  }

  filtered() {
    const normalizedQuery = this.query.toLowerCase();

    return this.cards.filter((card) => {
      if (normalizedQuery && !card.title.includes(normalizedQuery) && !card.searchableTags.includes(normalizedQuery)) return false;
      if (this.selectedNavGroups.size && !card.navGroups.some((navGroup) => this.selectedNavGroups.has(navGroup))) return false;
      if (this.selectedTags.size && !card.tags.some((tag) => this.selectedTags.has(tag))) return false;

      return Object.entries(this.ranges).every(([field, range]) => {
        if (!this.rangeIsActive(range)) return true;
        if (card[field] === null) return false;
        return card[field] >= Number(range.minInput.value) && card[field] <= Number(range.maxInput.value);
      });
    });
  }

  sortCards(list) {
    const [key, direction] = this.sort.split('-');
    const multiplier = direction === 'asc' ? 1 : -1;

    return list.sort((a, b) => {
      if (key === 'title') return a.title.localeCompare(b.title) * multiplier;
      return (a.date - b.date) * multiplier;
    });
  }

  resetVisibleAndRender() {
    this.visible = this.batch;
    this.render();
  }

  clearFilters() {
    this.query = '';
    if (this.searchInput) this.searchInput.value = '';
    this.querySelectorAll('[data-recipe-nav-group]:checked, [data-recipe-tag]:checked').forEach((input) => { input.checked = false; });
    this.selectedNavGroups.clear();
    this.selectedTags.clear();

    Object.entries(this.ranges).forEach(([field, range]) => {
      range.minInput.value = range.lowerBound;
      range.maxInput.value = range.upperBound;
      this.updateRange(field);
    });

    this.updateFiltersUrl();
    this.resetVisibleAndRender();
  }

  clearSingleFilter(type, value) {
    if (type === 'search') {
      this.query = '';
      if (this.searchInput) this.searchInput.value = '';
    }

    if (type === 'nav-group') {
      this.selectedNavGroups.delete(value);
      const input = Array.from(this.querySelectorAll('[data-recipe-nav-group]')).find((option) => option.value === value);
      if (input) input.checked = false;
    }

    if (type === 'tag') {
      this.selectedTags.delete(value);
      const input = Array.from(this.querySelectorAll('[data-recipe-tag]')).find((option) => option.value === value);
      if (input) input.checked = false;
    }

    if (type === 'range' && this.ranges[value]) {
      const range = this.ranges[value];
      range.minInput.value = range.lowerBound;
      range.maxInput.value = range.upperBound;
      this.updateRange(value);
    }

    this.updateFiltersUrl();
    this.resetVisibleAndRender();
  }

  updateFiltersUrl() {
    const url = new URL(window.location.href);

    const setListParam = (key, values) => {
      if (values.size) url.searchParams.set(key, Array.from(values).join(','));
      else url.searchParams.delete(key);
    };

    if (this.query) url.searchParams.set('recipe_q', this.query);
    else url.searchParams.delete('recipe_q');

    setListParam('recipe_type', this.selectedNavGroups);
    setListParam('recipe_tag', this.selectedTags);

    Object.entries(this.ranges).forEach(([field, range]) => {
      if (this.rangeIsActive(range)) {
        url.searchParams.set(`${field}_min`, range.minInput.value);
        url.searchParams.set(`${field}_max`, range.maxInput.value);
      } else {
        url.searchParams.delete(`${field}_min`);
        url.searchParams.delete(`${field}_max`);
      }
    });

    if (this.sort !== 'date-desc') url.searchParams.set('sort', this.sort);
    else url.searchParams.delete('sort');

    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  createFilterPill(label, type, value = '') {
    const pill = this.filterPillTemplate?.content.firstElementChild?.cloneNode(true);
    if (!pill) return null;

    pill.dataset.filterType = type;
    pill.dataset.filterValue = value;
    pill.querySelector('[data-recipe-filter-label]').textContent = label;
    return pill;
  }

  updateActiveFilters() {
    if (!this.activeFilters) return;

    const pills = [];
    const addPill = (label, type, value) => {
      const pill = this.createFilterPill(label, type, value);
      if (pill) pills.push(pill);
    };

    if (this.query) {
      const labelTemplate = window.KROWN?.settings?.collection_search_active_label;
      const label = labelTemplate ? labelTemplate.replace('[[terms]]', this.query) : `Search: ${this.query}`;
      addPill(label, 'search');
    }

    this.querySelectorAll('[data-recipe-nav-group]:checked').forEach((input) => {
      addPill(input.dataset.recipeLabel || input.value.replaceAll('-', ' '), 'nav-group', input.value);
    });

    this.querySelectorAll('[data-recipe-tag]:checked').forEach((input) => {
      addPill(input.dataset.recipeLabel || input.value.replaceAll('-', ' '), 'tag', input.value);
    });

    Object.entries(this.ranges).forEach(([field, range]) => {
      if (this.rangeIsActive(range)) addPill(`${range.label}: ${range.output.textContent}`, 'range', field);
    });

    this.activeFilters.replaceChildren(...pills);
    this.activeFilters.hidden = pills.length === 0;
  }

  render() {
    const list = this.sortCards(this.filtered());
    list.forEach((card) => this.grid.appendChild(card.el));

    const matches = new Set(list.map((card) => card.el));
    this.cards.forEach((card) => card.el.classList.toggle('is-hidden', !matches.has(card.el)));
    list.forEach((card, index) => card.el.classList.toggle('is-hidden', index >= this.visible));

    if (this.countEl) {
      const count = this.hasActiveFilters() ? list.length : this.total || list.length;
      this.countEl.textContent = `${count} ${count === 1 ? 'recipe' : 'recipes'}`;
    }

    this.emptyEl?.classList.toggle('is-visible', list.length === 0);
    if (this.moreBtn) {
      const hideMore = list.length <= this.visible;
      this.moreBtn.hidden = hideMore;
      if (this.moreContainer) this.moreContainer.hidden = hideMore;
    }
    if (this.clearBtn) this.clearBtn.hidden = !this.hasActiveFilters();
    this.updateActiveFilters();
  }
}

if (!customElements.get('recipe-hub')) {
  customElements.define('recipe-hub', RecipeHub);
}
